import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import axios from 'axios'

import { Grid, Typography, Divider } from '@material-ui/core'

import ActionModal from 'asterX-UI/Modals/ActionModal'
import { AutoCompleteField } from 'asterX-UI/Inputs'

import { AuthService, AUTH_API_URL } from 'lib/api'
import { useResErrors, useStateCallback } from 'hooks'
import { useHomeStore } from 'context/HomeStore'
import { useProfileStore } from 'context/ProfileStore'
import { useSnackbar } from 'notistack'

const RolesModal = ({ isRolesOpen, onClose }) => {
  const PROJECT = 'project'
  const BRANCH_LIST = 'branchList'
  const ROLE = 'role'

  const { enqueueSnackbar } = useSnackbar()

  const initialState = {
    [ROLE]: null,
    [PROJECT]: null,
    [BRANCH_LIST]: []
  }

  const { t } = useTranslation()
  const namespace = 'profilePage.accessInTheSystem.addEditAccessModal.'

  const { standardCheck } = useResErrors()
  const {
    homeStoreFunc,
    homeStore: { dictionary }
  } = useHomeStore()
  const {
    profileStore: { user = {} }
  } = useProfileStore()

  const [isFormValid, setIsFormValid] = useState([false, false, false, false])

  const [baseRoles, setBaseRoles] = useState([])
  const [roles, setRoles] = useState([])

  const [projectValues, setProjectValues] = useStateCallback(initialState)
  const [roleValues, setRoleValues] = useStateCallback(initialState)

  const userProjects =
    user?.roles?.length > 0 ? user?.roles?.map(item => item?.project?.code) : []

  const projects =
    user?.roles?.length > 0
      ? dictionary?.projects?.filter(prj => !userProjects.includes(prj?.code))
      : dictionary?.projects || []

  const [baseRolesProjects, setBaseRolesProjects] = useState(null)
  const [rolesProjects, setRolesProjects] = useState(null)

  const getRoles = (projectCode, type) => {
    AuthService.getRolesProject(projectCode).then(res =>
      standardCheck(
        res,
        () => {
          if (type === 'base') setBaseRoles(res.data || [])

          if (type === 'role') setRoles(res.data || [])
        },
        {
          onFail: () => {},
          textErr: t(namespace + 'errors.roles')
        }
      )
    )
  }

  const onChange = (e, type, isModule) => {
    const { name, value } = e.target

    if (type === 'base')
      setProjectValues(
        prevState => ({ ...prevState, [name]: value }),
        state => {
          if (name === PROJECT && !!value?.code && value?.code !== null)
            getRoles(state?.[PROJECT]?.code, type)
          if (isModule)
            setProjectValues(prevState => ({ ...prevState, [ROLE]: null }))
        }
      )

    if (type === 'role')
      setRoleValues(
        prevState => ({ ...prevState, [name]: value }),
        state => {
          if (name === PROJECT && !!value?.code && value?.code !== null)
            getRoles(state?.[PROJECT]?.code, type)
          if (isModule)
            setRoleValues(prevState => ({ ...prevState, [ROLE]: null }))
        }
      )
  }

  const isValid = () => {
    if (
      !!!projectValues.role?.code ||
      !!!projectValues.project?.code ||
      !!!roleValues.role?.code ||
      !!!roleValues.project?.code
    ) {
      let validArr = [false, false, false, false]

      if (!!!projectValues.project?.code) validArr[0] = true

      if (!!!projectValues.role?.code) validArr[1] = true

      if (!!!roleValues.project?.code) validArr[2] = true

      if (!!!roleValues.role?.code) validArr[3] = true

      setIsFormValid(validArr)
      return false
    }

    setIsFormValid([false, false, false, false])
    return true
  }

  const handleConfirm = () => {
    if (!isValid()) {
      enqueueSnackbar('Заполните все поля', {
        variant: 'error'
      })
      return
    }

    homeStoreFunc.setLoadingState(true)

    const headers = {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token')
      }
    }

    axios
      .post(
        AUTH_API_URL + '/users/bulkGrantRolesToUsers',
        {
          findByProjectCode: projectValues.project.code,
          addProjectCode: roleValues.project.code,
          findByRoleCode: projectValues.role.code,
          addRoleCode: roleValues.role.code
        },
        headers
      )
      .then(res => {
        homeStoreFunc.setLoadingState(false)
        if (res) {
          enqueueSnackbar(
            `Роль "${roleValues.role.name}" в модуле "${roleValues.project.name}" выдана ${res.data} пользователям`,
            {
              variant: 'success'
            }
          )

          setBaseRoles([])
          setRoles([])
          setProjectValues(initialState)
          setRoleValues(initialState)

          onClose()
        }
      })
      .catch(e => {
        console.error(e)
      })
  }

  const handleClose = () => {
    setBaseRoles([])
    setRoles([])
    setProjectValues(initialState)
    setRoleValues(initialState)
    setIsFormValid([false, false, false, false])

    onClose()
  }

  const sortAlphabetically = arr => {
    if (!Array.isArray(arr)) return []

    return arr.sort(function (a, b) {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    })
  }

  useEffect(() => {
    setBaseRolesProjects(projects.filter(el => !!el.name))
    setRolesProjects(projects.filter(el => !!el.name))
  }, [isRolesOpen])

  useEffect(() => {
    if (!!roleValues.project?.code)
      setBaseRolesProjects(
        projects.filter(el => el.code !== roleValues.project?.code && !!el.name)
      )

    if (!!projectValues.project?.code)
      setRolesProjects(
        projects.filter(
          el => el.code !== projectValues.project?.code && !!el.name
        )
      )
  }, [projectValues, roleValues])

  useEffect(() => {
    getRoles('UA')
  }, [])

  return (
    <>
      <ActionModal
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={handleConfirm}
        open={isRolesOpen}
        title={t('profilePage.giveRoles')}
        labelConfirm={t('confirm')}
        labelCancel={t('cancel')}
        isRolesModal={true}
      >
        <Grid container xs={12}>
          <Grid item container wrap="nowrap" direction="column" spacing={3}>
            <Grid item xs={12}>
              <Typography component={'h3'}>
                {t('profilePage.whichRoleBased')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <AutoCompleteField
                name={PROJECT}
                error={isFormValid[0]}
                value={projectValues?.[PROJECT]}
                options={sortAlphabetically(baseRolesProjects)}
                onChange={e => onChange(e, 'base', true)}
                valueKeys={['code', 'name']}
                label={t(namespace + PROJECT)}
                placeholder={t(namespace + PROJECT)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <AutoCompleteField
                name={ROLE}
                error={isFormValid[1]}
                value={projectValues?.[ROLE]}
                options={sortAlphabetically(baseRoles)}
                onChange={e => onChange(e, 'base')}
                valueKeys={['id', 'code', 'name']}
                label={t(namespace + ROLE)}
                placeholder={t(namespace + ROLE)}
                fullWidth
              />
            </Grid>
            <Grid item style={{ padding: '10px 0' }}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography component={'h3'}>
                {t('profilePage.whichRole')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <AutoCompleteField
                name={PROJECT}
                error={isFormValid[2]}
                value={roleValues?.[PROJECT]}
                options={sortAlphabetically(rolesProjects)}
                onChange={e => onChange(e, 'role', true)}
                valueKeys={['code', 'name']}
                label={t(namespace + PROJECT)}
                placeholder={t(namespace + PROJECT)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <AutoCompleteField
                name={ROLE}
                error={isFormValid[3]}
                value={roleValues?.[ROLE]}
                options={sortAlphabetically(roles)}
                onChange={e => onChange(e, 'role')}
                valueKeys={['id', 'code', 'name']}
                label={t(namespace + ROLE)}
                placeholder={t(namespace + ROLE)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </ActionModal>
    </>
  )
}

export default RolesModal
